import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { scriptsAPI } from '../services/api';

const Scripts = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [isHTMLMode, setIsHTMLMode] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await scriptsAPI.getScripts();
      setScripts(response.data);
    } catch (error) {
      message.error('获取脚本列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingScript(null);
    form.resetFields();
    setIsHTMLMode(false);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingScript(record);
    form.setFieldsValue(record);
    setIsHTMLMode(record.is_html || false);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await scriptsAPI.deleteScript(id);
      message.success('删除成功');
      fetchScripts();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingScript) {
        await scriptsAPI.updateScript(editingScript.id, values);
        message.success('更新成功');
      } else {
        await scriptsAPI.createScript(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchScripts();
    } catch (error) {
      if (error.response) {
        message.error('操作失败');
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '脚本名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '代码模式',
      dataIndex: 'is_html',
      key: 'is_html',
      render: (isHTML) => (
        <Tag color={isHTML ? 'blue' : 'default'}>
          {isHTML ? 'HTML代码' : '纯JS脚本'}
        </Tag>
      ),
    },
    {
      title: '脚本URL/代码',
      dataIndex: 'script_url',
      key: 'script_url',
      ellipsis: true,
      render: (text, record) => (
        <span style={{ fontFamily: record.is_html ? 'monospace' : 'inherit' }}>
          {record.is_html && text.length > 50 ? text.substring(0, 50) + '...' : text}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加脚本
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={scripts}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editingScript ? '编辑脚本' : '添加脚本'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="脚本名称"
            rules={[{ required: true, message: '请输入脚本名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="is_html"
            label="代码模式"
            valuePropName="checked"
            initialValue={false}
            extra="开启后可输入HTML代码，系统将自动转换为JS代码"
          >
            <Switch 
              checkedChildren="HTML代码" 
              unCheckedChildren="纯JS脚本"
              onChange={(checked) => setIsHTMLMode(checked)}
            />
          </Form.Item>

          <Form.Item
            name="script_url"
            label={isHTMLMode ? "HTML代码" : "JS代码"}
            rules={[{ required: true, message: isHTMLMode ? '请输入HTML代码' : '请输入JS代码' }]}
            extra={isHTMLMode ? "输入完整的HTML代码，将自动转换为JS" : "输入完整的JS代码（IIFE格式）"}
          >
            <Input.TextArea 
              rows={isHTMLMode ? 8 : 10} 
              placeholder={isHTMLMode ? 
                '例如：<div id="ad"><script src="https://example.com/ad.js"></script></div>' :
                `(function() {
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.setAttribute('data-cfasync', 'false');
    script.src = '//example.com/ad.js';
    (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
})();`
              }
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="是否启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Scripts;
